import React from "react";
import { Link } from "react-router-dom";
import { formatTime, toTitleCase, makeRecoveryString, getPhotoLocation } from "../utils";
import "./Card.css";

const STATIC_MEDIA_HOST = "https://boostertracker-static.s3.amazonaws.com";
const STARSHIP_FALLBACK_IMAGE = "/media/rocket_pad_photos/Starship_OLPA_launches_photo.jpg";

const FALCON_STOCK_IMAGES = [
  "/media/rocket_pad_photos/Falcon_LC39A_launches_photo.jpg",
  "/media/rocket_pad_photos/Falcon_SLC40_launches_photo.jpg",
  "/media/rocket_pad_photos/Falcon_SLC4E_launches_photo.jpg",
  "/media/rocket_pad_photos/Falcon_Heavy_launches_photo.jpg",
  "/media/rocket_photos/falcon9.jpeg",
  "/media/rocket_photos/Falconheavy.jpeg",
  "/media/pad_photos/lc39a.jpeg",
  "/media/pad_photos/slc_40.jpeg",
  "/media/pad_photos/slc_4e.jpeg",
];

const STARSHIP_STOCK_IMAGES = [
  "/media/rocket_pad_photos/Starship_OLPA_launches_photo.jpg",
  "/media/rocket_photos/Starship.jpeg",
  "/media/pad_photos/IMG_4349.jpeg",
  "/media/pad_photos/olp_a_qknNfEa.jpeg",
];

const formatEnumValue = (value, fallback = "Unknown") =>
  toTitleCase(String(value || fallback).replace(/_/g, " ")).replace("Tbd", "TBD");

const cleanImagePath = (image) => {
  if (!image) {
    return "";
  }

  return String(image)
    .replace(/^http:\/\/127\.0\.0\.1:8000\/?/, "/")
    .replace(/^https?:\/\/boostertracker-static\.s3\.amazonaws\.com/i, "");
};

const getImageUrl = (image) => {
  const imagePath = cleanImagePath(image);

  if (!imagePath) {
    return "";
  }

  if (/^https?:\/\//.test(imagePath)) {
    return imagePath;
  }

  const mediaPath = imagePath.startsWith("/") ? imagePath : `/${imagePath}`;

  if (mediaPath.startsWith("/media/")) {
    if (typeof window !== "undefined" && window.location.hostname === "localhost") {
      return `${getPhotoLocation()}${mediaPath}`;
    }
    return `${STATIC_MEDIA_HOST}${mediaPath}`;
  }

  return `${getPhotoLocation()}${mediaPath}`;
};

const isFalconPadFallback = (image) => /\/media\/rocket_pad_photos\/Falcon_/i.test(String(image || ""));
const isGenericStageImage = (image) =>
  !image || /\/media\/stage_photos\/default_booster\.jpg$/i.test(cleanImagePath(image));
const isPadStockImage = (image) => /\/media\/rocket_pad_photos\//i.test(cleanImagePath(image));

const hashString = (value) => {
  const str = String(value || "");
  let hash = 0;

  for (let index = 0; index < str.length; index += 1) {
    hash = (hash * 31 + str.charCodeAt(index)) >>> 0;
  }

  return hash;
};

const pickStockImage = (object, rocket) => {
  const isStarship = rocket?.name === "Starship";
  // Family-scoped pools only — Falcon never draws from Starship art and vice versa.
  const pool = isStarship ? STARSHIP_STOCK_IMAGES : FALCON_STOCK_IMAGES;
  const rocketImage = rocket?.image ? cleanImagePath(rocket.image) : "";
  const options =
    rocketImage && !pool.includes(rocketImage) && (isStarship || !/starship/i.test(rocketImage))
      ? [rocketImage, ...pool]
      : [...pool];
  const index = hashString(object?.id ?? object?.name) % Math.max(options.length, 1);

  return options[index] || pool[0];
};

const getObjectImage = (object, objectType, subObjects = {}) => {
  const rocket = subObjects.rockets?.find((item) => String(item.id) === String(object?.rocket));

  // Keep unique booster / vehicle photos.
  if (object?.image && !isGenericStageImage(object.image)) {
    return object.image;
  }

  // Stages without a unique photo: diversify so cards don't all share default_booster.jpg.
  if (objectType === "stage") {
    return pickStockImage(object, rocket);
  }

  return object?.image || rocket?.image || "";
};

const getLaunchImage = (launch, subObjects = {}) => {
  const rocket = subObjects.rockets?.find((item) => String(item.id) === String(launch?.rocket));
  const stagesById = subObjects.stagesById || {};

  // Prefer a unique booster photo from the flight when the launch only has pad stock art.
  if (!launch?.image || isPadStockImage(launch.image) || isFalconPadFallback(launch.image)) {
    const recoveries = launch?.stage_and_recoveries?.length
      ? launch.stage_and_recoveries
      : launch?.recoveries || [];

    for (const recovery of recoveries) {
      const stageId = recovery?.stage?.id ?? recovery?.stage;
      const stage = recovery?.stage?.image ? recovery.stage : stagesById[String(stageId)];
      const stageImage = stage?.image;

      if (stageImage && !isGenericStageImage(stageImage)) {
        return stageImage;
      }
    }
  }

  if (rocket?.name === "Starship" && (!launch?.image || isFalconPadFallback(launch.image))) {
    return STARSHIP_FALLBACK_IMAGE;
  }

  // Unique mission photo (rare) or pad/rocket fallback.
  if (launch?.image && !isPadStockImage(launch.image)) {
    return launch.image;
  }

  return (
    launch?.image ||
    subObjects.pads?.find((pad) => String(pad.id) === String(launch?.pad))?.image ||
    rocket?.image ||
    pickStockImage(launch, rocket)
  );
};

// Function to set card data for different object types
const setCardData = (object, objectType, subObjects) => {
  const cardData = {};

  switch (objectType) {
    case "launch":
      const recoveryString = makeRecoveryString(object.recoveries, subObjects.zones);

      cardData["Rocket: "] = subObjects.rockets.find((rocket) => rocket.id === object.rocket)?.name;
      cardData["Liftoff Time: "] = formatTime(object.time);
      cardData["Launch Site: "] = subObjects.pads.find((pad) => pad.id === object.pad)?.nickname;
      cardData[`Stage${object.stages_string?.includes("and") ? "s" : "" || ""}: `] = object.stages_string;
      cardData[`Landing Location${recoveryString?.includes("and") ? "s" : ""}: `] = recoveryString;
      cardData[`Outcome: `] = formatEnumValue(object.launch_outcome, "TBD");
      break;

    case "stage":
      cardData["Rocket: "] = subObjects.rockets.find((rocket) => rocket.id === object.rocket)?.name;
      cardData["Type: "] = formatEnumValue(object.type);
      cardData["Status: "] = formatEnumValue(object.status);
      cardData["Version: "] = object.version;
      cardData["Number of Launches: "] = object.num_launches;
      break;

    case "spacecraft":
      cardData["Nickname: "] = object.nickname || "N/A";
      cardData["Type: "] = formatEnumValue(object.type);
      cardData["Status: "] = formatEnumValue(object.status);
      cardData["Version: "] = object.version;
      cardData["Number of Launches: "] = object.num_launches;
      break;

    case "spacecraftInfo":
      cardData["Time: "] = formatTime(object.launch.time.slice(0, -1));
      cardData["Splashdown Time: "] = object.splashdown_time ? formatTime(object.splashdown_time?.slice(0, -1)) : "TBD";
      cardData["Turnaround Time: "] = object.spacecraft_turnaround
        ? `${Math.round((object.spacecraft_turnaround / 86400) * 100) / 100} days`
        : "None";
      cardData["Recovery Ship: "] = subObjects.boats.find((boat) => boat.id === object.recovery_boat)?.name || "TBD";
      break;

    case "stageInfo":
      cardData["Time: "] = formatTime(object.launch.time.slice(0, -1));
      cardData["Turnaround Time: "] = object.stage_turnaround
        ? `${Math.round((object.stage_turnaround / 86400) * 100) / 100} days`
        : "None";
      cardData["Landing Method: "] = `${formatEnumValue(object.method).replace("Drone Ship", "ASDS")} (${formatEnumValue(
        object.method_success,
        "TBD"
      )})`;
      cardData["Landing Location: "] =
        subObjects.zones.find((zone) => zone.id === object.landing_zone)?.nickname || "N/A";
      break;

    case "pad":
      cardData["Nickname: "] = object.nickname;
      cardData["Location: "] = object.location;
      cardData["Launches: "] = object.num_launches;
      cardData["Fastest Turnaround: "] = object.fastest_turnaround;
      cardData["Status: "] = formatEnumValue(object.status);
      break;

    case "recoveryzone":
      cardData["Nickname: "] = object.nickname;
      if (object.serial_number) {
        cardData["Boat ID: "] = object.serial_number;
      }
      cardData["Landings: "] = object.num_landings;
      cardData["Fastest Turnaround: "] = object.fastest_turnaround;
      cardData["Status: "] = formatEnumValue(object.status);
      break;

    case "padInfo":
      cardData["Rocket: "] = subObjects.rockets.find((rocket) => rocket.id === object.rocket)?.name;
      cardData["Liftoff Time: "] = formatTime(object.time.slice(0, -1));
      cardData["Launch Site: "] = subObjects.pads.find((pad) => pad.id === object.pad)?.nickname;
      cardData["Pad Turnaround: "] = object.pad_turnaround
        ? `${Math.round((object.pad_turnaround / 86400) * 100) / 100} days`
        : "None";
      cardData[`Outcome: `] = formatEnumValue(object.launch_outcome, "None");
      break;

    case "zoneInfo":
      cardData["Time: "] = formatTime(object.launch.time.slice(0, -1));
      cardData["Turnaround Time: "] = object.zone_turnaround
        ? `${Math.round((object.zone_turnaround / 86400) * 100) / 100} days`
        : "None";
      cardData["Launch Site: "] = subObjects.pads.find((pad) => pad.id === object.launch.pad)?.nickname;
      cardData["Landing Location: "] =
        subObjects.zones.find((zone) => zone.id === object.landing_zone)?.nickname || "N/A";
      break;

    default:
      break;
  }

  return cardData;
};

const InformationCard = ({ object, objectType, subObjects, additionalInfo }) => {
  let url = `/${objectType}/${object.id}/`;
  const objectImageUrl = getImageUrl(getObjectImage(object, objectType, subObjects));
  let backgroundImage = objectImageUrl ? `url(${objectImageUrl})` : undefined;
  let name = object.name;

  // Set additional info
  const cardData = setCardData(object, objectType, subObjects);

  if (objectType === "spacecraftInfo" || objectType === "stageInfo" || objectType === "zoneInfo") {
    url = `/launch/${object.launch.id}/`;
    const launchImage = getImageUrl(getLaunchImage(object.launch, subObjects));
    backgroundImage = launchImage ? `url(${launchImage})` : undefined;
    name = object.launch.name;
  } else if (objectType === "padInfo") {
    url = `/launch/${object.id}/`;
    name = object.name;
  }

  if (additionalInfo) {
    for (const [key, value] of Object.entries(additionalInfo)) {
      cardData[key] = value;
    }
  }

  return (
    <Link to={url} className="launch-link">
      <div className="launch-card">
        <div
          className="launch-image"
          style={{
            backgroundImage: backgroundImage,
          }}
        ></div>
        <div className="launch-info">
          <h1>{name}</h1>
          {Object.entries(cardData || {}).map(([key, value]) => (
            <p key={key}>
              <b>{key}</b>
              {value}
            </p>
          ))}
        </div>
      </div>
    </Link>
  );
};

export default InformationCard;
